import { useState } from "react";
import { newsletterData } from "@/mocks/homeData";

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    const formData = new FormData(e.currentTarget);
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt") {
        payload.append(key, String(value));
      }
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d9noqje8mbnljin5a3mg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setEmail("");
      } else {
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section className="w-full py-16 md:py-20 bg-primary-500">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mb-3">
            {newsletterData.headline}
          </h2>
          <p className="text-background-200 leading-relaxed mb-8">
            {newsletterData.subheadline}
          </p>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
          >
            <input
              type="text"
              name="company_alt"
              className="honeypot-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              placeholder={newsletterData.placeholder}
              required
              className="flex-1 px-5 py-3.5 text-sm rounded-md bg-background-50 text-foreground-950 placeholder-foreground-500 border-0 outline-none focus:ring-2 focus:ring-accent-400"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="px-6 py-3.5 text-sm font-semibold rounded-md bg-accent-500 text-foreground-950 hover:bg-accent-400 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
            >
              {status === "submitting" ? "Subscribing..." : newsletterData.cta}
            </button>
          </form>

          {status === "success" && (
            <p className="mt-4 text-sm text-background-200">
              Thank you for subscribing!
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 text-sm text-accent-300">{errorMsg}</p>
          )}

          <p className="mt-4 text-xs text-background-300/80">
            {newsletterData.privacy}
          </p>
        </div>
      </div>
    </section>
  );
}